import { Button, Flex, Icon, IconButton, Menu, MenuButton, MenuItem, MenuList, Text, Image } from '@chakra-ui/react';
import { createColumnHelper } from '@tanstack/react-table';
import { getConfigFromStorage } from '@/utils/config';
import { formatDateToDateTimeDisplay } from '@/utils/formatDateToDateTimeDisplay';
import { CustomTable } from '@/components/CustomTable/CustomTable';
import { ChakraFeatherIcon } from '@/components/ChakraFeatherIcon';
import { EllipsisVerticalIcon } from '@heroicons/react/24/outline';
import { Polls, PollsInteractivity, translatePollsInteractivity, translatePollsStatus } from '@/model/polls';
import { useTranslation } from 'react-i18next';

export interface PollsTableProps {
  data: Polls[];
  isLoading: boolean;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
  totalItems: number;
  sorting?: { field: string; desc: boolean };
  onSortingChange?: (field: string, desc: boolean) => void;
  onPaginationChange: (page: number, pageSize: number) => void;
  onFinishClick?: (item: Polls) => void;
  onViewResultClick?: (item: Polls) => void;
  onEditClick?: (item: Polls) => void;
  onDeleteClick?: (item: Polls) => void;
}

export const PollsTable = ({
  data,
  isLoading,
  pageNumber,
  pageSize,
  totalPages,
  sorting,
  totalItems,
  onSortingChange,
  onPaginationChange,
  onFinishClick,
  onViewResultClick,
  onEditClick,
  onDeleteClick,
}: PollsTableProps) => {
  const { t } = useTranslation();
  const columnHelper = createColumnHelper<Polls>();
  const isMultiTeam = getConfigFromStorage()?.is_multi_team ?? true;

  const columns = [
    columnHelper.accessor('created_at', {
      cell: props => formatDateToDateTimeDisplay(props.getValue()),
      header: t('pages.application_polls.components.polls_table.polls_table_created_at_header'),
    }),
    columnHelper.accessor('team_name', {
      cell: props => {
        const { team_image, team_name } = props.row.original;
        return (
          <Flex alignItems='center' gap={2}>
            {team_image ? <Image src={team_image} alt={team_name || ''} w='24px' h='24px' objectFit='contain' /> : null}
            <Text fontWeight='bold'>{team_name || ''}</Text>
          </Flex>
        );
      },
      header: t('pages.application_polls.components.polls_table.polls_table_team_header'),
    }),
    columnHelper.accessor('updated_at', {
      cell: props => formatDateToDateTimeDisplay(props.getValue() ?? ''),
      header: t('pages.application_polls.components.polls_table.polls_table_updated_at_header'),
    }),
    columnHelper.accessor('question', {
      cell: props => props.getValue(),
      header: t('pages.application_polls.components.polls_table.polls_table_question_header'),
    }),
    columnHelper.accessor('identifier', {
      cell: props => props.getValue(),
      header: t('pages.application_polls.components.polls_table.polls_table_identifier_header'),
    }),    
    columnHelper.accessor('total_participants', {
      cell: props => props.getValue(),
      header: t('pages.application_polls.components.polls_table.polls_table_total_participants_header'),
    }),
    columnHelper.accessor('total_comments', {
      cell: props => props.getValue(),
      header: t('pages.application_polls.components.polls_table.polls_table_total_comments_header'),
    }),
    columnHelper.accessor('status', {
      cell: props => translatePollsStatus(props.getValue()),
      header: t('pages.application_polls.components.polls_table.polls_table_status_header'),
    }),
    columnHelper.accessor('interactivity', {
      cell: props => translatePollsInteractivity(props.getValue()),
      header: t('pages.application_polls.components.polls_table.polls_table_interactivity_header'),
    }),
    columnHelper.display({
      id: 'justify-expenses',
      header: '',
      cell: props => {
        const isEnabled = props.row.original.interactivity === PollsInteractivity.ENABLED;

        if (!isEnabled) {
          return (
            <Button
              onClick={() => onViewResultClick?.(props.row.original as Polls)}
              variant='link'
              color='brand.500'
              textDecor='underline'
              display='flex'
              alignItems='center'
              gap={2}
            >
              {t('pages.application_polls.components.polls_table.polls_table_view_result')}
            </Button>
          );
        }

        return (
          <Button
            onClick={() => onFinishClick?.(props.row.original as Polls)}
            variant='link'
            color='brand.500'
            textDecor='underline'
            display='flex'
            alignItems='center'
            gap={2}
          >
            {t('pages.application_polls.components.polls_table.polls_table_finish_button')}
          </Button>
        );
      },
      enableSorting: false,
      meta: {
        align: 'left',
      },
    }),
    columnHelper.display({
      id: 'actions',
      header: t('pages.application_polls.components.polls_table.polls_table_actions_header'),
      cell: props => (
        <Flex justifyContent={'end'}>
          <Menu>
            <MenuButton
              textColor='text.table'
              as={IconButton}
              variant='ghost'
              size='sm'
              icon={<Icon as={EllipsisVerticalIcon} />}
              aria-label={t('pages.application_polls.components.polls_table.polls_table_menu_button_label')}
            />
            <MenuList textColor='white'>
              <MenuItem
                onClick={() => onEditClick?.(props.row.original as Polls)}
                icon={
                  <Text>
                    <ChakraFeatherIcon name='edit-2' w='18px' />
                  </Text>
                }
              >
                <Text>{t('pages.application_polls.components.polls_table.polls_table_edit_menu_item')}</Text>
              </MenuItem>

              <MenuItem
                onClick={() => onDeleteClick?.(props.row.original as Polls)}
                icon={
                  <Text>
                    <ChakraFeatherIcon name='trash-2' color='red.500' w='18px' />
                  </Text>
                }
              >
                <Text color='red.500'>{t('pages.application_polls.components.polls_table.polls_table_delete_menu_item')}</Text>
              </MenuItem>
            </MenuList>
          </Menu>
        </Flex>
      ),
      enableSorting: false,
      meta: {
        align: 'right',
      },
    }),
  ];

  return (
    <CustomTable
      data={data}
      isLoading={isLoading}
      pageNumber={pageNumber}
      pageSize={pageSize}
      totalPages={totalPages}
      totalItems={totalItems}
      columns={isMultiTeam ? columns : columns.filter(col => (col as any).accessorKey !== 'team_name')}
      onPaginationChange={onPaginationChange}
      sorting={sorting}
      onSortingChange={onSortingChange}
    />
  );
};
